import { useEffect, useRef, useState } from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import { Box, FormControl, FormGroup, FormLabel, Grid, Typography } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { ControlledCheckbox, ControlledTextField } from 'components/common'
import { CardTitle } from 'components/common/controls'
import { ConfirmDialog } from 'components/common/Dialog'
import {
  Driver,
  DriverAppSettingsDtoInput,
  useGetDriverQuery,
  useUpdateDriverAppSettingsMutation,
} from 'generated/graphql'

import notificationValidationSchema from './ValidationSchema'

type NotificationForm = Omit<DriverAppSettingsDtoInput, 'driverId' | 'notificationRadiusKm'> & {
  notificationRadiusKm: string
}

interface NotificationDetailsProps {
  driverId: number
}

type NotificationSettingsSource = Pick<
  Driver,
  | 'isEmailNotification'
  | 'isEmailNotificationForNewAvailableBookings'
  | 'isMobileAppNotification'
  | 'isMobileNotificationForNewAvailableBookings'
  | 'isMobileNotificationForHgvJobs'
  | 'isMobileNotificationForNonHgvJobs'
  | 'notificationRadiusKm'
>

const getDriverNotificationSettings = (driver: NotificationSettingsSource): NotificationForm => ({
  isEmailNotification: Boolean(driver.isEmailNotification),
  isEmailNotificationForNewAvailableBookings: Boolean(
    driver.isEmailNotificationForNewAvailableBookings,
  ),
  isMobileAppNotification: Boolean(driver.isMobileAppNotification),
  isMobileNotificationForNewAvailableBookings: Boolean(
    driver.isMobileNotificationForNewAvailableBookings,
  ),
  isMobileNotificationForHgvJobs: Boolean(driver.isMobileNotificationForHgvJobs),
  isMobileNotificationForNonHgvJobs: Boolean(driver.isMobileNotificationForNonHgvJobs),
  notificationRadiusKm: String(driver.notificationRadiusKm ?? 20),
})

const NotificationDetails = ({ driverId }: NotificationDetailsProps) => {
  const { data, loading } = useGetDriverQuery({
    variables: { driverId },
    fetchPolicy: 'cache-first',
  })
  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
    watch,
  } = useForm<NotificationForm>({
    resolver: joiResolver(notificationValidationSchema),
    mode: 'onChange',
    defaultValues: {
      isEmailNotification: false,
      isEmailNotificationForNewAvailableBookings: false,
      isMobileAppNotification: false,
      isMobileNotificationForNewAvailableBookings: false,
      isMobileNotificationForHgvJobs: false,
      isMobileNotificationForNonHgvJobs: false,
      notificationRadiusKm: '20',
    },
  })
  const history = useHistory()
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false)
  const pendingPath = useRef<string | null>(null)
  const unblockRef = useRef<(() => void) | null>(null)
  const mobileEnabled = watch('isMobileAppNotification')
  const emailEnabled = watch('isEmailNotification')

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  useEffect(() => {
    if (isDirty) {
      unblockRef.current = history.block((location) => {
        pendingPath.current = location.pathname + location.search + location.hash
        setUnsavedDialogOpen(true)
        return false
      })
    } else {
      unblockRef.current?.()
      unblockRef.current = null
    }

    return () => {
      unblockRef.current?.()
      unblockRef.current = null
    }
  }, [isDirty, history])

  const handleConfirmNavigation = () => {
    unblockRef.current?.()
    unblockRef.current = null
    setUnsavedDialogOpen(false)
    if (pendingPath.current) {
      history.push(pendingPath.current)
      pendingPath.current = null
    }
  }

  const handleCancelNavigation = () => {
    pendingPath.current = null
    setUnsavedDialogOpen(false)
  }

  useEffect(() => {
    if (data?.driver) {
      const driver = data.driver as Driver
      const settings = getDriverNotificationSettings(driver)
      reset(settings, { keepDirty: false, keepTouched: false, keepErrors: false })
    }
  }, [data?.driver, reset])

  const [updateSettings, { loading: updating }] = useUpdateDriverAppSettingsMutation({
    onCompleted: ({ updateDriverAppSettings }) => {
      reset(getDriverNotificationSettings(updateDriverAppSettings))
      toast.success('Notification settings updated successfully')
    },
    onError: (error) => toast.error(error.message),
  })

  const onSubmit = (settings: NotificationForm) => {
    updateSettings({
      variables: {
        input: {
          driverId: String(driverId),
          ...settings,
          notificationRadiusKm: Number(settings.notificationRadiusKm),
        },
      },
    })
  }

  return (
    <>
      <CardTitle>Notification Details</CardTitle>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
          <FormControl fullWidth>
            <FormLabel>Email notifications</FormLabel>
            <FormGroup>
              <ControlledCheckbox
                control={control}
                name='isEmailNotification'
                label='Receive email notifications'
              />
              <ControlledCheckbox
                control={control}
                name='isEmailNotificationForNewAvailableBookings'
                label='Receive email notifications for new available jobs'
                disabled={!emailEnabled}
              />
            </FormGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
          <FormControl fullWidth>
            <FormLabel>App notifications</FormLabel>
            <FormGroup>
              <ControlledCheckbox
                control={control}
                name='isMobileAppNotification'
                label='Enable notifications'
              />
              <ControlledCheckbox
                control={control}
                name='isMobileNotificationForNewAvailableBookings'
                label='Receive push notifications for new available jobs'
                disabled={!mobileEnabled}
              />
            </FormGroup>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 3 }}>
            <FormLabel>Job Notifications</FormLabel>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              Choose how you&apos;d like to be notified about new available jobs.
              <br />
              You&apos;ll only receive notifications for jobs you&apos;re eligible to complete.
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1, mb: 2 }}>
              Eligibility is based on:
              <br />
              Your vehicle type
              <br />
              The distance between your selected notification radius and the job location
              (collection or delivery)
            </Typography>
            <ControlledTextField
              control={control}
              name='notificationRadiusKm'
              label='Notification radius (km)'
              type='text'
              min='20'
              maxLength={3}
              inputMode='numeric'
              error={Boolean(errors.notificationRadiusKm)}
              helperText={errors.notificationRadiusKm?.message}
              fullWidth
            />
            <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
              I would like to receive notifications if my base address or my current location is
              within {watch('notificationRadiusKm') || '-'} km from the collection or delivery
              addresses.
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1, mb: 1 }}>
              A larger radius means you&apos;ll receive notifications for more jobs, including those
              further away.
            </Typography>
            <Typography variant='body2' color='text.secondary' paragraph>
              A smaller radius reduces the number of notifications but may mean you miss some
              opportunities.
            </Typography>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <FormLabel>Vehicle Types</FormLabel>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              I would like to receive notifications for:
            </Typography>
            <FormGroup>
              <ControlledCheckbox
                control={control}
                name='isMobileNotificationForNonHgvJobs'
                label='Up to Luton vehicles'
                disabled={!mobileEnabled}
              />
              <ControlledCheckbox
                control={control}
                name='isMobileNotificationForHgvJobs'
                label='HGVs'
                disabled={!mobileEnabled}
              />
            </FormGroup>
          </FormControl>
        </Grid>
      </Grid>
      <Box display='flex' justifyContent='flex-end' pt={2}>
        <LoadingButton
          loading={updating}
          disabled={!isDirty || loading || updating}
          variant='contained'
          onClick={handleSubmit(onSubmit)}>
          Save
        </LoadingButton>
      </Box>
      <ConfirmDialog
        title='Unsaved Changes'
        message='You have unsaved changes. Are you sure you want to leave? Your changes will be lost.'
        open={unsavedDialogOpen}
        setOpen={setUnsavedDialogOpen}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        option1='Stay'
        option2='Leave'
      />
    </>
  )
}

export default NotificationDetails
